'use client'

import { CartItemList } from '@/feature/cart/components/cart-item-list/CartItemList'
import { useIsImpersonating } from '@/lib/next/authentication/impersonating.client'
import { useApolloClient } from '@apollo/client'
import { getOperationName } from '@apollo/client/utilities'
import { gql } from '@gql-generated/gql'
import type { CompanyList_CurrentUserAccountsFragment } from '@gql-generated/graphql'
import { ApproveesSelectDocument, ApproversSelectDocument } from '@gql-generated/graphql'
import { useCallback, useTransition } from 'react'
import type { SelectUserActionInput } from '../../services/account.action'
import { SelectCompanyCard } from '../select-company-card/SelectCompanyCard.client'

const ApproversSelectOperationName = getOperationName(ApproversSelectDocument)
const ApproveesSelectOperationName = getOperationName(ApproveesSelectDocument)

export function useRefreshApprovalFlowCurrentUser() {
  const client = useApolloClient()

  return useCallback(() => {
    if (!ApproveesSelectOperationName || !ApproversSelectOperationName) {
      console.error('Failed to refetch query ApproveesSelect')
    } else {
      client.refetchQueries({
        include: [ApproversSelectOperationName, ApproveesSelectOperationName],
      })
    }
  }, [client])
}

gql(/* GraphQL */ `
  fragment CompanyList_PointOfSale on PointOfSale {
    name
    phoneNumber
    id
    address {
      line1
      city
    }
    subsidiary {
      id
    }
  }

  fragment CompanyList_Company on Company {
    code
    name
    id
    pointOfSale {
      ...CompanyList_PointOfSale
    }
  }

  fragment CompanyList_CurrentUserAccounts on User {
    id
    email
    company {
      ...CompanyList_Company
    }
    favoritePointOfSale {
      id
      name
    }
  }
  query CompanyListCurrentUserAccounts {
    currentUserAccounts(status: ACTIVE) {
      ...CompanyList_CurrentUserAccounts
    }
  }
`)

type CompanyListProps = {
  accounts: CompanyList_CurrentUserAccountsFragment[]
  activeCompanyCode?: string
  onAccountSelected: (account: SelectUserActionInput) => Promise<void>
}

export function CompanyList({ activeCompanyCode, accounts, ...props }: CompanyListProps) {
  const [isPending, startTransition] = useTransition()
  const isImpersonating = useIsImpersonating()

  const { onAccountSelected } = props

  const handleAccountSelected = useCallback(
    async (account: SelectUserActionInput) => {
      if (isImpersonating) {
        return
      }

      startTransition(async () => {
        await onAccountSelected(account)
      })
    },
    [isImpersonating, onAccountSelected],
  )

  return (
    <CartItemList className="h-auto w-full space-y-1">
      {/* User Accounts */}
      {accounts.map(
        (account) =>
          account.company && (
            <CartItemList.Item key={account.company.code}>
              <SelectCompanyCard
                account={account}
                onSelect={handleAccountSelected}
                isLoading={isPending}
                isCurrentCompany={account.company.code === activeCompanyCode}
              />
            </CartItemList.Item>
          ),
      )}
    </CartItemList>
  )
}
